import clsx from 'clsx';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

interface DraggableRowProps extends React.HTMLAttributes<HTMLDivElement> {
    /** The items to be displayed in the draggable row */
    items: React.ReactElement[];
}

const DraggableRow = ({ items, ...props }: DraggableRowProps) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ dragFree: true });
    const [isListOverflowing, setIsListOverflowing] = useState(false);

    const onScrollChange = useCallback((emblaApi: EmblaCarouselType) => {
        const slidesInView = emblaApi.slidesInView();
        const lastIndex = emblaApi.slideNodes().length - 1;
        setIsListOverflowing(!slidesInView.includes(lastIndex));
    }, []);

    useEffect(() => {
        if (items.length && emblaApi) {
            emblaApi.on('scroll', onScrollChange);
            emblaApi.on('slidesInView', onScrollChange);
            onScrollChange(emblaApi);

            return () => {
                emblaApi?.off('scroll', onScrollChange);
                emblaApi?.off('slidesInView', onScrollChange);
            };
        }
    }, [emblaApi, items.length, onScrollChange]);

    return (
        <div {...props} className={clsx('relative flex-auto min-w-0', props.className)}>
            <div className="overflow-x-hidden min-h-[235px]" ref={emblaRef}>
                <div className="flex gap-8 touch-pan-y touch-pinch-zoom px-1 cursor-grab active:cursor-grabbing scroll-auto">
                    {items}
                </div>
            </div>
            <div
                className={clsx(
                    'absolute top-0 right-0 h-full w-[5%] bg-gradient-to-r from-transparent to-white pointer-events-none',
                    {
                        hidden: !isListOverflowing,
                    }
                )}
            />
        </div>
    );
};

export { DraggableRow };
